import { forwardRef } from "react";
import type { KeyboardEvent } from "react";
import { Icon } from "@common/index";

interface SearchInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    onClear: () => void;
    // Runs BEFORE the Enter → onSubmit default. A handler that consumed the key (ArrowUp/Down,
    // or Enter on a highlighted suggestion) calls preventDefault and the submit is skipped, so
    // Enter with nothing highlighted still submits the query as it always has.
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    // Combobox wiring (#227): whether the suggestion listbox is showing, its id, and the id of
    // the highlighted option (undefined when none is).
    isExpanded: boolean;
    listboxId: string;
    activeDescendantId: string | undefined;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    (
        {
            value,
            onChange,
            onSubmit,
            onClear,
            onKeyDown,
            isExpanded,
            listboxId,
            activeDescendantId,
        },
        ref,
    ) => (
        <div className="relative flex-1">
            <Icon
                name="search"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-neutral/60 pointer-events-none"
            />
            {/* type="text" rather than "search": ARIA in HTML permits role="combobox" on the
                former only. enterKeyHint keeps the mobile keyboard's Search key, and the
                clear control below already replaces the native one "search" would draw. */}
            <input
                ref={ref}
                type="text"
                enterKeyHint="search"
                autoComplete="off"
                role="combobox"
                aria-label="Search"
                aria-autocomplete="list"
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? listboxId : undefined}
                aria-activedescendant={activeDescendantId}
                value={value}
                placeholder="Search..."
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    // An IME is composing (Japanese, Chinese, Korean…): Enter confirms the
                    // composition and the arrows move inside it. Neither the highlight
                    // handler nor the submit below may react — hence guarded here, above
                    // both, rather than inside the passthrough alone.
                    if (e.nativeEvent.isComposing) return;
                    onKeyDown(e);
                    if (e.defaultPrevented) return;
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
                className="w-full h-10 pl-10 pr-10 rounded-md border border-base-300 bg-base-100 text-neutral placeholder:text-neutral/40 focus:outline-none focus:ring-2 focus:ring-secondary"
            />
            {value.length > 0 && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onClick={onClear}
                    className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle text-secondary"
                >
                    <Icon name="cancel" filled className="text-lg" />
                </button>
            )}
        </div>
    ),
);

SearchInput.displayName = "SearchInput";
